import { ArrowRight } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { usePublishedPressReleases } from '@/lib/api/hooks/press-release'

interface Article {
  id?: string
  title: string
  date: string
  image: string
  href: string
}

interface ArticleCardProps {
  id?: string
  title: string
  date: string
  image: string
  href: string
}

const ArticleCard = ({ title, date, image, href }: ArticleCardProps) => (
  <article className="p-2 group cursor-pointer">
    <Link to={href} className="block">
      <div className="mb-2">
        <span className="text-sm font-medium font-satoshi text-gray-600">
          {date}
        </span>
      </div>
      
      <div className="flex gap-4">
        <img 
          src={image}
          alt={title}
          className="w-24 h-24 object-cover flex-shrink-0"
        />
        
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-normal font-pt-serif text-gray-900 leading-relaxed group-hover:text-wine-100 transition-colors line-clamp-3">
            {title}
          </h3>
        </div>
      </div>
    </Link>
  </article>
)

interface RecentArticlesProps {
  className?: string
}

// Format date helper
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  const day = date.getDate()
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear().toString().slice(-2)
  return `${day} ${month} '${year}`
}

const RecentArticles = ({ className }: RecentArticlesProps) => {
  const navigate = useNavigate()


  const { data: pressReleasesData, isLoading } = usePublishedPressReleases(
    { page: 1, size: 3 },
    {
      landingPage: false,
      pressRelease: true,
      pressReleaseBanner: false
    }
  )

  const handleViewAll = () => {
    navigate('/media/press-release')
  }

  // Transform API data to component format
  const articles: Article[] = (pressReleasesData?.data?.data || []).map((pr) => ({
    id: pr.pressReleaseId,
    title: pr.title,
    date: pr.datePublished ? formatDate(pr.datePublished) : formatDate(pr.createdAt),
    image: pr.coverImage,
    href: `/media/press-release/${pr.pressReleaseId}`
  }))

  return (
    <section className={className}>
      <div className="space-y-6">
        <h2 className="text-2xl font-normal font-pt-serif text-gray-900 leading-7">
          RECENT PRESS RELEASES
        </h2>
        
        {isLoading ? (
          <div className="space-y-0">
            {[1, 2, 3].map((index) => (
              <div key={index} className="p-2 animate-pulse">
                <div className="mb-2 h-4 bg-gray-200 rounded w-20"></div>
                <div className="flex gap-4">
                  <div className="w-24 h-24 bg-gray-200 rounded flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : articles.length > 0 ? (
          <div className="space-y-0">
            {articles.map((article) => (
              <ArticleCard key={article.id || article.href} {...article} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No recent press releases available</p>
        )}
        
        <button 
          onClick={handleViewAll}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-900 hover:text-wine-100 transition-colors group"
        >
          <span className="text-base font-medium font-satoshi underline">
            View all
          </span>
          <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </section>
  )
}

export default RecentArticles